import { DocumentTypeInputFilter } from "domain/models/document-type-input-filter";
import { translateText } from "infra/translation/translate";
import DocumentTypeForm from "presentation/components/DocumentType/DocumentTypeForm";
import Spinner from "presentation/components/Shared/Spinner";
import {
  useDeleteDocumentTypeMutation,
  useDocumentTypeByFilterAll,
} from "presentation/hooks/use-query-document-type";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import type { DocumentType } from "domain/entities/document-type";
import { InputIcon } from "primereact/inputicon";
import { IconField } from "primereact/iconfield";

const DocumentTypeGrid: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [documentType, setDocumentType] = useState<DocumentType>();
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [isFormVisible, setIsFormVisible] = useState(false);

  const {
    data: documentTypesData,
    isLoading,
    isError,
    error,
  } = useDocumentTypeByFilterAll({
    limit: 100,
    sortBy: "name",
    sortOrder: "asc",
  } as DocumentTypeInputFilter);

  const { mutate: deleteDocumentType } = useDeleteDocumentTypeMutation();

  const handleDelete = (documentTypeId: string) => {
    deleteDocumentType(documentTypeId, {
      onSuccess: () => {
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Tipo de Documento eliminado com sucesso.",
          life: 3000,
        });
      },
      onError: (error: any) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail:
            error.response?.data?.message ||
            "Ocorreu um erro ao eliminar o tipo de documento.",
          life: 3000,
        });
      },
    });
  };

  const confirmDelete = (documentTypeId: string) => {
    confirmDialog({
      message: "Tem a certeza que deseja eliminar este tipo de documento?",
      header: "Confirmação de Eliminação",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Sim, eliminar",
      rejectLabel: "Não",
      accept: () => handleDelete(documentTypeId),
    });
  };

  const contractTypesBodyTemplate = (rowData: DocumentType) => {
    if (!rowData.contractTypes || rowData.contractTypes.length === 0) {
      return <Tag key={0} value={"N/A"} severity={"danger"} />;
    }
    return (
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        {rowData.contractTypes.map((ct, index) => (
          <Tag key={index} value={ct} severity={"info"} />
        ))}
      </div>
    );
  };

  const actionBodyTemplate = (rowData: DocumentType) => {
    return (
      <div className="spacing-icons">
        <i
          className="pi pi-pencil cursor-pointer"
          onClick={() => {
            setDocumentType(rowData);
            setIsFormVisible(true);
          }}
        />
        <i
          className="pi pi-trash cursor-pointer"
          onClick={() => confirmDelete(rowData.id!)}
        />
      </div>
    );
  };

  const handleHideForm = () => {
    setIsFormVisible(false);
    setDocumentType(undefined);
  };

  const header = (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Pesquisar por nome"
        />
      </IconField>

      <Button
        icon="pi pi-plus"
        label="Novo Tipo de Documento"
        onClick={() => {
          setDocumentType({} as DocumentType);
          setIsFormVisible(true);
        }}
      />
    </div>
  );

  if (isLoading) return <Spinner />;
  if (isError) return <p>Ocorreu um erro: {error.message}</p>;

  return (
    <div className="card">
      <DataTable
        value={documentTypesData?.data || []}
        paginator
        rows={10}
        size="small"
        emptyMessage="Nenhum tipo de documento encontrado."
        globalFilter={globalFilter}
        globalFilterFields={["name"]}
        header={header}
      >
        <Column
          field="name"
          header={translateText("Name", "pt")}
          sortable
          style={{ width: "45%" }}
        />
        <Column
          header={translateText("Contract Type", "pt")}
          body={contractTypesBodyTemplate}
          style={{ width: "45%" }}
        />
        <Column body={actionBodyTemplate} style={{ width: "8rem" }} />
      </DataTable>
      <Toast ref={toast} />
      <ConfirmDialog />
      <DocumentTypeForm
        visible={isFormVisible}
        onHide={handleHideForm}
        documentType={documentType}
      />
    </div>
  );
};
export default DocumentTypeGrid;
